import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { addData } from '../../services/firestoreService';
import { trackEvent, getAttribution } from '../../analytics/tracker';
import { Mail, Phone, Send, MapPin } from 'lucide-react';

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: '',
    hp_check: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.hp_check) return;

    if (formData.name.trim().length < 2) {
      toast('Validation Error', 'Name is too short.', 'error');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast('Validation Error', 'Please enter a valid email address.', 'error');
      return;
    }
    if (!formData.service) {
      toast('Validation Error', 'Please select a service.', 'error');
      return;
    }
    if (formData.message.trim().length < 10) {
      toast('Validation Error', 'Message is too short.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const attribution = getAttribution();
      await addData('websiteRequests', {
        name: formData.name,
        email: formData.email,
        service: formData.service,
        message: formData.message,
        status: 'new',
        sourceData: {
          source: attribution.source,
          medium: attribution.medium,
          campaign: attribution.campaign,
          referrer: attribution.referrer || '',
          visitorId: attribution.visitorId,
          sessionId: attribution.sessionId
        }
      });
      void trackEvent('contact_submit', undefined, { label: formData.service });
      toast('Success', 'Your request has been logged. Expect a callback soon.', 'success');
      setFormData({ name: '', email: '', service: '', message: '', hp_check: '' });
    } catch (err) {
      console.error(err);
      toast('System Error', 'Failed to send message. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="contact"
      className="sw-section"
      role="region"
      aria-labelledby="contact-title"
    >
      <div className="sw-container">
        <div className="section-header text-center">
          <span className="sw-eyebrow" aria-hidden="true">{t('nav_contact')}</span>
          <h2 id="contact-title">{t('contact_title')}</h2>
          <p className="section-desc">{t('contact_desc')}</p>
        </div>

        <div className="contact-wrapper sw-glass">
          <div className="contact-info-panel">
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text)' }}>{t('contact_title')}</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--text-soft)' }}>{t('contact_desc')}</p>

            <ul className="direct-contact" aria-label="Contact information">
              <li className="contact-item">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)' }}>
                  <Mail size={18} />
                </div>
                <div className="item-text">
                  <span className="item-label">{t('label_email_us')}</span>
                  <a
                    href="mailto:swiftechlb@gmail.com"
                    aria-label="Send email to swiftechlb@gmail.com"
                    onClick={() => trackEvent('outbound_link', undefined, { label: 'email', url: 'mailto:swiftechlb@gmail.com' })}
                  >
                    swiftechlb@gmail.com
                  </a>
                </div>
              </li>
              <li className="contact-item">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(var(--success-rgb), 0.1)', color: 'var(--success)' }}>
                  <Phone size={18} />
                </div>
                <div className="item-text">
                  <span className="item-label">{t('label_call_us')}</span>
                  <a
                    href="tel:+96176637752"
                    aria-label="Call +961 76637752"
                    onClick={() => trackEvent('outbound_link', undefined, { label: 'phone', url: 'tel:+96176637752' })}
                  >
                    +961 76637752
                  </a>
                </div>
              </li>
              <li className="contact-item">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(var(--accent-rgb), 0.1)', color: 'var(--accent)' }}>
                  <MapPin size={18} />
                </div>
                <div className="item-text">
                  <span className="item-label">Location</span>
                  <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Lebanon</span>
                </div>
              </li>
            </ul>

            <ul className="social-panel mt-6" aria-label="Social media links">
              <li>
                <a
                  href="https://www.instagram.com/swiftech.lb"
                  target="_blank"
                  rel="noreferrer"
                  className="social-link"
                  aria-label="Follow us on Instagram"
                  onClick={() => trackEvent('outbound_link', undefined, { label: 'instagram', url: 'https://www.instagram.com/swiftech.lb' })}
                >
                  IG
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/share/19QxwFmSrm/"
                  target="_blank"
                  rel="noreferrer"
                  className="social-link"
                  aria-label="Follow us on Facebook"
                  onClick={() => trackEvent('outbound_link', undefined, { label: 'facebook', url: 'https://www.facebook.com/share/19QxwFmSrm/' })}
                >
                  FB
                </a>
              </li>
              <li>
                <a
                  href="https://www.tiktok.com/@swiftech.lb"
                  target="_blank"
                  rel="noreferrer"
                  className="social-link"
                  aria-label="Follow us on TikTok"
                  onClick={() => trackEvent('outbound_link', undefined, { label: 'tiktok', url: 'https://www.tiktok.com/@swiftech.lb' })}
                >
                  TK
                </a>
              </li>
            </ul>
          </div>

          <div className="contact-form-panel">
            <form
              onSubmit={handleSubmit}
              className="sw-form"
              aria-label="Contact form"
              noValidate
            >
              {/* Honeypot */}
              <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <input
                  type="text"
                  name="hp_check"
                  value={formData.hp_check}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="form-row">
                <div className="input-group">
                  <label htmlFor="contact-name">{t('label_name')}</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    aria-required="true"
                    autoComplete="name"
                    placeholder="Your name"
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="contact-email-input">{t('label_email')}</label>
                  <input
                    type="email"
                    id="contact-email-input"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    aria-required="true"
                    autoComplete="email"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="contact-service">{t('label_service')}</label>
                <select
                  id="contact-service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  aria-required="true"
                >
                  <option value="" disabled>{t('placeholder_service')}</option>
                  <option value="Business Website">{t('opt_business')}</option>
                  <option value="QR Menu">{t('opt_qr_menu')}</option>
                  <option value="E-Commerce">{t('opt_ecommerce')}</option>
                  <option value="Custom Platform">{t('opt_custom')}</option>
                  <option value="Redesign">{t('opt_redesign')}</option>
                </select>
              </div>
              <div className="input-group">
                <label htmlFor="contact-message">{t('label_message')}</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  aria-required="true"
                  rows={5}
                  placeholder="Tell us about your project..."
                ></textarea>
              </div>
              <button
                type="submit"
                className="sw-btn sw-btn-primary btn-full"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                    {t('sending') || 'Sending...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send size={16} />
                    {t('btn_send_message')}
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
